import React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Badge, Button, Modal } from 'antd';
import { EditOutlined, CloseCircleOutlined } from '@ant-design/icons';
import './ProductCard.css';

const { confirm } = Modal;

const ProductCard = ({
  product,
  onStatusChange,
  onClose,
  showActions = true,
  showAuthor = false,
  isAdmin = false,
  statuses = ['active', 'pending', 'closed'],
  statusChangeLoading = null,
  showStatus = false,
  getLocationName = null,
  editLink = null
}) => {
  const API = (process.env.REACT_APP_API ).replace(/\/api$/, '');

  //format currency (padrão brasileiro)
  Number.prototype.format = function (n, x) {
    return this.toFixed(Math.max(0, ~~n)).replace(/\d(?=(\d{3})+(?!\d))/g, '$&.').replace(/\.(\d{2})$/, ',$1');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'closed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Ativo';
      case 'pending': return 'Pendente';
      case 'closed': return 'Vendido';
      default: return status;
    }
  };

  return (
    <div className="modern-product-card">
      {/* Imagem com aspect ratio */}
      <div className="product-image-container">
        <Link to={`/product/${product._id}`} className="text-decoration-none product-link">
          <img
            src={product.images?.[0] ? `${API}${product.images[0]}` : '/default-image.jpg'}
            alt={product.name}
            className="product-image"
            loading="lazy"
          />
        </Link>
        {/* Badge de imagens */}
        <div className="image-badge">
          <Badge
            count={product.images?.length || 0}
            style={{
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              borderRadius: '12px',
              fontSize: '12px',
              boxShadow: '0 2px 8px #f0f0f0',
              border: 'none',
            }}
            showZero={false}
          />
        </div>
        {/* Badge de status */}
        {showStatus && (
          <div className="status-badge">
            <span
              className={`badge rounded-pill px-2 py-1 status-${product.status}`}
            >
              {getStatusText(product.status)}
            </span>
          </div>
        )}
      </div>
      {/* Conteúdo do produto */}
      <div className="product-content">
        {/* Nome e preço */}
        <div className="product-header">
          <div className="product-title-section">
            <Link to={`/product/${product._id}`} className='text-decoration-none'>
              <h6 className='card-title mb-1 text-dark fw-bold product-title'>
                {product.name}
              </h6>
            </Link>
            <h5 className='text-success fw-bold mb-0 product-price'>
              {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(product.price)}
            </h5>
          </div>
          {/* Ações */}
          {showActions && (
            <div className='d-flex gap-1 flex-shrink-0 align-items-center'>
              {onStatusChange && (
                <select
                  className='form-select form-select-sm shadow-none mb-0 status-select'
                  aria-label='Selecionar status'
                  value={product.status}
                  onChange={e => onStatusChange(e, product._id)}
                  disabled={statusChangeLoading === product._id}
                >
                  <option value={product.status} disabled>{getStatusText(product.status)}</option>
                  {Array.isArray((typeof statuses !== 'undefined' ? statuses : [])) && (statuses || ['active', 'pending', 'closed'])
                    .filter(s => s !== product.status)
                    .map((s, i) => (
                      <option key={i} value={s}>{getStatusText(s)}</option>
                    ))}
                </select>
              )}
              {editLink && (
                <Link
                  to={editLink}
                  className='btn btn-warning btn-sm close-btn'
                  title="Editar Produto"
                  style={{ marginRight: '4px' }}
                >
                  <EditOutlined className="close-icon" /> Editar
                </Link>
              )}
              {product.status !== 'closed' && onClose && (
                <button
                  className='btn btn-outline-primary btn-sm close-btn'
                  title="Marcar como Vendido"
                  onClick={(e) => {
                    e.preventDefault();
                    confirm({
                      className: 'centered-confirm-modal',
                      title: 'Marcar como Vendido',
                      content: 'Tem certeza que deseja marcar este produto como vendido?',
                      okText: 'Sim',
                      cancelText: 'Não',
                      onOk() {
                        onClose(product._id);
                      },
                    });
                  }}
                >
                  <i className="fas fa-check-circle me-1"></i> Vendido
                </button>
              )}
            </div>
          )}
        </div>
        {/* Tags de categoria e condição */}
        <div className='d-flex flex-wrap gap-1 mb-1 badges-section'>
          <Link
            to={`/category/${product.category?._id}`}
            className='badge bg-light text-dark text-decoration-none rounded-pill category-badge'
          >
            {product.category?.name}
          </Link>
          <span
            className='badge bg-light text-dark rounded-pill condition-badge'
          >
            {product.condition === 'new' ? 'Novo' :
              product.condition === 'used' ? 'Usado' :
                product.condition === 'not_applicable' ? 'Não se aplica' : product.condition}
          </span>
        </div>
        {/* Footer com localização, autor e data */}
        <div className="product-footer">
          <div className="location-section">
            <Link
              className='text-decoration-none'
            >
              <small className='text-muted d-block location-text'>
                <i className='fas fa-map-marker-alt me-1'></i>
                {getLocationName
                  ? getLocationName(product.location)
                  : (typeof product.location === 'object' ? product.location?.name : product.location)
                }
              </small>
            </Link>
            {showAuthor && product.author && (
              <Link
                to={`/user/${product.author._id}`}
                className='text-decoration-none'
              >
                <small className='text-muted author-text'>
                  <i className='fas fa-user me-1'></i>
                  {product.author.name}
                </small>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
